import { useState, useEffect } from "react";

// react-router components
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";
// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

import MDAvatar from "components/MDAvatar";
import ProfileModal from "components/custom/ProfileModal";
import NotificationBell from "components/NotificationBell";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import MDTypography from "components/MDTypography";
import { capitalizeFirstLetterSafe } from "utils";
import { Divider } from "@mui/material";
import { handleSignout } from "utils/Logout";
import { getDecodedUserData } from "utils/Constant";
import SettingsModal from "components/custom/SettingsModal";

function DashboardNavbar({ absolute, light, isMini, hidebreadcrumbTitle }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const userName = getDecodedUserData()?.username
  const role = getDecodedUserData()?.role;
  const navigate = useNavigate();


  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleSetting = () => {
    handleCloseMenu();
    setOpenSettings(true);
  };
  const handleProfile = () => {
    handleCloseMenu();
    setProfileModalOpen(true);
  };
  const handleSignouts = () => {
    handleCloseMenu();

    handleSignout(); // clears token, localStorage, etc.

    navigate("/sign-in", { replace: true });
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        onClick={handleProfile}
        icon={<Icon>account_circle</Icon>}
        title="View Profile"
      />
      <Divider
        sx={{
          my: 0.5,
          borderColor: "rgba(0,0,0,0.08)",
        }}
      />

      {/* <NotificationItem
        icon={<Icon>notifications</Icon>}
        title="Notifications"
      />
      <Divider
        sx={{
          my: 0.5,
          borderColor: "rgba(0,0,0,0.08)",
        }}
      /> */}

      <NotificationItem
        onClick={handleSetting}
        icon={<Icon>settings</Icon>}
        title="Settings"
      />

      {/*  Divider before Sign Out */}
      <Divider
        sx={{
          my: 0.5,
          borderColor: "rgba(0,0,0,0.08)",
        }}
      />

      <NotificationItem
        onClick={handleSignouts}
        icon={<Icon>logout</Icon>}
        title="Sign Out"
      />
    </Menu>
  );


  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      style={{ zIndex: "999" }}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} hideTitle={hidebreadcrumbTitle} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })} display="flex" alignItems="center">

            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={navbarMobileMenu}
              onClick={handleMiniSidenav}
            >
              <Icon sx={iconsStyle} fontSize="medium">
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>

            <NotificationBell darkMode={darkMode} />

            <MDBox display="flex" alignItems="center" sx={{ cursor: "pointer" }} onClick={handleOpenMenu}>
              <MDBox display="flex" flexDirection="column" textAlign="right" lineHeight="0.6rem">
                <MDTypography variant="h6" fontWeight="medium" sx={{ mr: 1 }}>
                  {capitalizeFirstLetterSafe(userName)}
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular" sx={{ mr: 1 }}>
                  {role}
                </MDTypography>
              </MDBox>
              <MDAvatar
                src="/path-to-avatar.jpg"
                alt={userName?.toUpperCase()}
                size="md"
                shadow="md"
                bgColor="success"
              />
            </MDBox>
            {renderMenu()}
            <ProfileModal open={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
            <SettingsModal open={openSettings} onClose={() => setOpenSettings(false)} />
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
